import { useState } from "react";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";

import { ApiError } from "../api/client";
import { createSchedule, type PlanningProject, type ScheduleSummary } from "../api/schedules";
import { useToken } from "../auth/sessionContext";
import { strings } from "../brand/strings";
import { ServerField } from "../lib/serverErrors";
import { ActionDialog } from "../nav/ActionDialog";
import { PICKER_CLASS, PickerOption, pickerWidth } from "../nav/PickerOption";

export interface CreateScheduleDialogProps {
  readonly projects: readonly PlanningProject[];
  readonly initialProjectId?: string | undefined;
  readonly initialYear?: number | undefined;
  readonly onClose: () => void;
  readonly onCreated: (created: ScheduleSummary) => void;
}

export function CreateScheduleDialog({
  projects,
  initialProjectId,
  initialYear,
  onClose,
  onCreated,
}: CreateScheduleDialogProps): React.JSX.Element {
  const token = useToken();
  const currentYear = new Date().getFullYear();
  const [projectId, setProjectId] = useState(
    initialProjectId && projects.some((p) => p.id === initialProjectId)
      ? initialProjectId
      : (projects[0]?.id ?? ""),
  );
  const [year, setYear] = useState<number | "">(initialYear ?? currentYear);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const defaultTitle = year ? `${String(year)} inspection plan` : "";

  async function submit(): Promise<void> {
    if (!projectId || !year) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const created = await createSchedule(token, {
        projectId,
        year,
        title: title.trim().length > 0 ? title.trim() : defaultTitle,
      });
      onCreated(created);
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        setError(err);
      } else {
        setError(new ApiError("unavailable", 500, null));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <ActionDialog
      open
      title={strings.schedules.createScheduleHeading}
      error={error}
      onClose={onClose}
    >
      <div className="q-field">
        <label className="q-field__label" htmlFor="create-schedule-project">
          {strings.schedules.projectSelectLabel}
        </label>
        <ServerField field="project_id">
          <NativeSelect
            id="create-schedule-project"
            data-testid="create-schedule-project"
            className={PICKER_CLASS}
            value={projectId}
            onChange={(event) => {
              setProjectId(event.target.value);
            }}
          >
            <option value="" />
            {projects.map((one) => (
              <PickerOption
                key={one.id}
                value={one.id}
                identifier={one.humanId}
                name={one.name}
                width={pickerWidth(projects.map((p) => p.humanId))}
              />
            ))}
          </NativeSelect>
        </ServerField>
      </div>

      <div className="q-field">
        <label className="q-field__label" htmlFor="create-schedule-year">
          {strings.schedules.yearSelectLabel}
        </label>
        <ServerField field="year">
          <Input
            id="create-schedule-year"
            data-testid="create-schedule-year"
            type="number"
            min={1990}
            max={2200}
            value={year}
            onChange={(event) => {
              const val = event.target.value;
              setYear(val === "" ? "" : parseInt(val, 10));
            }}
          />
        </ServerField>
      </div>

      <div className="q-field">
        <label className="q-field__label" htmlFor="create-schedule-title">
          {strings.schedules.planTitleLabel}
        </label>
        <ServerField field="title">
          <Input
            id="create-schedule-title"
            data-testid="create-schedule-title"
            value={title}
            placeholder={defaultTitle}
            onChange={(event) => {
              setTitle(event.target.value);
            }}
          />
        </ServerField>
      </div>

      <div className="q-actions-row">
        <button
          type="button"
          className="q-button q-mini--primary"
          data-testid="create-schedule-submit"
          disabled={busy || !projectId || !year}
          onClick={() => {
            void submit();
          }}
        >
          {strings.schedules.createSchedule}
        </button>
        <button type="button" className="q-button q-button--quiet" onClick={onClose}>
          {strings.common.cancel}
        </button>
      </div>
    </ActionDialog>
  );
}
